import { useCallback, useContext, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { UserContext } from "../../../../hooks/UserContext";
import type { Branch, Deal, Job } from "../../../../models";
import { defaultJobForImport } from "../../../../models";
import {
	BoundaryChangeType,
	BoundaryChangeTypes,
	CSVImportRows,
	GeoFileType,
	JobsPriority,
	JobsPriorities,
	KeyedShapefileMap,
} from "../../../../utilities/types";
import { FileDownloader } from "../../../../utilities/downloadFile";
import { ImportFormProps } from "../../shared/ImportHelpers";
import { CSVImportFormViewModel } from "./ImportForm_CSV.types";

export function useImportFormCSVViewModel(props: ImportFormProps): CSVImportFormViewModel {
	const userContext = useContext(UserContext);

	// Internal Branch/Deal selection state
	const [internalSelectedBranch, setInternalSelectedBranch] = useState<Branch | null>(
		props.selectedBranches?.[0] ?? null,
	);
	const [internalSelectedDeal, setInternalSelectedDeal] = useState<Deal | null>(props.selectedDeals?.[0] ?? null);

	// CSV state
	const [transformedKeyedShapefileMap, setTransformedKeyedShapefileMap] = useState<KeyedShapefileMap>(new Map());
	const [loading, setLoading] = useState(false);
	const [jobData, setJobData] = useState<Job[]>([]);
	const [selectableFiles, setSelectableFiles] = useState<Record<string, Record<GeoFileType, File[]>>>({});
	const [originalShapefiles, setOriginalShapefiles] = useState<File[]>([]);
	const [keyedShapefileMap, setKeyedShapefileMap] = useState<KeyedShapefileMap>(new Map());
	const [csvImportType, setCsvImportType] = useState<"ROGO" | "3rdParty">("3rdParty");

	// Computed values
	const canShowBranchSelector = useMemo(() => !!props.branches && props.branches.length > 0, [props.branches]);
	const canShowImporter = useMemo(() => !!internalSelectedDeal, [internalSelectedDeal]);

	// Branch change handler
	const handleInternalBranchChange = useCallback((branch: Branch | null) => {
		setInternalSelectedBranch(branch);
		setInternalSelectedDeal(null);
		resetCSVState();
	}, []);

	// Deal change handler - keeps existing job data unchanged per user decision
	const handleInternalDealChange = useCallback((deal: Deal | null) => {
		setInternalSelectedDeal(deal);
	}, []);

	const importCSV = async (rows: CSVImportRows[]) => {
		if (!internalSelectedDeal) {
			toast.error("Please select a sampling configuration before importing");
			return;
		}

		const results: Job[] = [];
		const deal = internalSelectedDeal;
		let ignoredRows = 0;

		for (const row of rows) {
			if (rows.length === 1) {
				const row = rows[0];
				if (row["growerName"] === undefined || row["growerName"].includes("\n") || row["growerName"] === "") {
					ignoredRows++;
					break;
				}
				if (row["fieldName"] === undefined || row["fieldName"].includes("\n") || row["fieldName"] === "") {
					ignoredRows++;
					break;
				}
			}

			if (!row.growerName && !row.fieldName) {
				ignoredRows++;
				continue;
			}

			const dealId = row.deal ? undefined : deal.id;
			const density = row.gridSize ? parseFloat(row.gridSize) : deal.density;
			const testPackage = row.testPackage || deal.testPackage;
			const depth = row.depth ? parseFloat(row.depth) : deal.depth;
			const micros = row.freqOfMicros ? parseFloat(row.freqOfMicros) : deal.frequencyOfMicros;

			if (row.fieldPriority && !JobsPriorities.includes(row.fieldPriority as JobsPriority)) {
				toast.error(`Invalid priority: ${row.fieldPriority}`, { autoClose: 10000 });
				throw new Error(`Invalid priority: ${row.fieldPriority}`);
			}
			if (row.boundaryChange && !BoundaryChangeTypes.includes(row.boundaryChange as BoundaryChangeType)) {
				toast.error(`Invalid boundary change type: ${row.boundaryChange}`, { autoClose: 10000 });
				throw new Error(`Invalid boundary change type: ${row.boundaryChange}`);
			}

			const newJob = defaultJobForImport(row.fieldName, depth, density, row.growerName, micros, testPackage);

			newJob.branchIds = internalSelectedBranch ? [internalSelectedBranch.id] : [];
			newJob.boundaryChangeType = !row.boundaryChange
				? undefined
				: (row.boundaryChange as BoundaryChangeType | undefined);
			newJob.dealIds = dealId ? [dealId] : [];
			newJob.importDepth = depth || 0;
			newJob.importFieldName = row.fieldName || "";
			newJob.importGrowerName = row.growerName || "";
			newJob.importFarmName = row.farmName || "";
			newJob.externalId = "";
			newJob.fieldId = row.fieldID || "";
			newJob.fieldPriority = (row.fieldPriority as JobsPriority) || "Regular Turn (default)";
			newJob.externalFrequencyOfMicros = micros || undefined;
			newJob.importDensity = density || 0;
			newJob.growerPhoneNumber = row.growerCell || "";
			newJob.jobFlags = [row.jobFlags || "Bulk Upload"];
			newJob.externalTestPackageId = testPackage;
			newJob.labSubmissionCode = row.regularLabSubmissionCode || "";
			newJob.isPointsAttached = ["y", "yes", "true"].includes(row.pointsAttached?.toLowerCase() || "No");
			newJob.isReadyToSample = ["y", "yes", "true"].includes(row.ready?.toLowerCase() || "No");
			newJob.labInstructions = row.specialLabInstructions || "";
			newJob.submitterEmail = row.submitterEmail ?? userContext?.email ?? "";
			newJob.submitterName = row.submitterName ?? userContext?.fullName ?? "";
			newJob.eventId = row.syncCode;

			results.push(newJob);
		}

		if (results.length != 0) {
			setJobData((prevData) => [...prevData, ...results]);
			toast.success(`Parsed ${results.length} rows`);
		}

		if (ignoredRows > 0) {
			toast.warn(`Ignored ${ignoredRows} rows with no grower or field name`);
		}
	};

	async function downloadBulkUploadTemplate() {
		await FileDownloader.downloadURL(
			"https://airtableuploads.s3.us-east-2.amazonaws.com/bulk_upload_template.csv",
			"Bulk Upload Template.csv",
		);
	}

	function resetCSVState() {
		setTransformedKeyedShapefileMap(new Map());
		setLoading(false);
		setJobData([]);
		setSelectableFiles({});
		setOriginalShapefiles([]);
		setKeyedShapefileMap(new Map());
		setCsvImportType("3rdParty");
	}

	return {
		// Context
		userContext,

		// Internal Branch/Deal selection
		internalSelectedBranch,
		internalSelectedDeal,
		canShowBranchSelector,
		canShowImporter,
		handleInternalBranchChange,
		handleInternalDealChange,

		// State
		jobData,
		setJobData,
		loading,
		setLoading,
		selectableFiles,
		setSelectableFiles,
		originalShapefiles,
		setOriginalShapefiles,
		keyedShapefileMap,
		setKeyedShapefileMap,
		transformedKeyedShapefileMap,
		setTransformedKeyedShapefileMap,
		csvImportType,
		setCsvImportType,

		// Business logic
		importCSV,
		downloadBulkUploadTemplate,
		resetCSVState,
	};
}
